/**
 * Parameter encoding for the generated API clients.
 *
 * The generator's own encoder only knows `date-time`: it calls `toISOString()` for that and
 * `String(value)` for everything else. A `format: date` parameter is typed as `Date` but
 * falls into the `String(value)` branch, so a real `Date` reaches the server as
 * `Mon Jan 13 2026 00:00:00 GMT+0100 (CET)` and the request fails with a 400.
 *
 * This encoder handles `date` as the calendar day it is, taken in the local zone so a day
 * picked on screen is the day that is sent — `toISOString()` would shift it to the previous
 * day for any clock east of UTC, which is every clock we run in.
 *
 * Install it on a client's `Configuration` as `encodeParam`.
 */
export function encodeApiParam(param: {
  name: string;
  value: unknown;
  in: string;
  style: string;
  explode: boolean;
  dataType: string;
  dataFormat: string | undefined;
}): string {
  const { value, dataFormat } = param;

  if (value instanceof Date) {
    return encodeURIComponent(dataFormat === 'date' ? toLocalIsoDay(value) : value.toISOString());
  }

  return encodeURIComponent(String(value));
}

/**
 * The same calendar day, for a `format: date` field inside a request BODY.
 *
 * The encoder above only ever sees path and query parameters - the generated client hands
 * bodies straight to `HttpClient`, which serialises them with `JSON.stringify`, so a `Date`
 * goes out through `toJSON()` -> `toISOString()` and arrives a day early on every clock east
 * of UTC. The backend field is a `LocalDate`, so what it wants is `2026-01-13`, not an
 * instant that happens to fall on that day somewhere.
 *
 * The cast is the point of this function: the generated DTO types the field as `Date`
 * because that is what the generator does with `format: date`, but the wire format is a
 * plain day string. Casting here, once and explained, beats doing it at every call site.
 */
export function apiDateBody(date: Date): Date {
  return toLocalIsoDay(date) as unknown as Date;
}

function toLocalIsoDay(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
